import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { meApi } from "@/lib/api";
import { clearSignInAttempt } from "@/lib/signInGuard";
import { unavailableCode } from "@/lib/accountStatus";
import { SignInProblem } from "@/pages/SignInProblem";
import { Toaster } from "sonner";
import { useAuth } from "react-oidc-context";
import { ThemeProvider, useTheme } from "@/hooks/use-theme";
import { MainLayout } from "@/components/layout/MainLayout";
import LoginPage from "@/pages/LoginPage";
import OverviewPage from "@/pages/OverviewPage";
import TransportsPage from "@/pages/config/TransportsPage";
import AuthsPage from "@/pages/config/AuthsPage";
import AorsPage from "@/pages/config/AorsPage";
import IdentifiesPage from "@/pages/config/IdentifiesPage";
import ContextsPage from "@/pages/config/ContextsPage";
import ContextDetailPage from "@/pages/config/ContextDetailPage";
import ExtensionsPage from "@/pages/config/ExtensionsPage";
import AriAppsPage from "@/pages/config/AriAppsPage";
import WebsocketEndpointsPage from "@/pages/config/WebsocketEndpointsPage";
import HepSettingsPage from "@/pages/config/HepSettingsPage";
import ConfigViewPage from "@/pages/config/ConfigViewPage";
import ActiveCallsPage from "@/pages/calls/ActiveCallsPage";
import CallHistoryPage from "@/pages/calls/CallHistoryPage";
import AuditLogsPage from "@/pages/settings/AuditLogsPage";
import AmiConsolePage from "@/pages/admin/AmiConsolePage";
import LogsPage from "@/pages/LogsPage";
import { runtimeSession } from "@/runtime/session";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  // /api/me is the identity call in both sign-in modes. A 403 there means
  // "account unavailable" (never back to sign-in); success ends the loop guard.
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => meApi.get(),
    enabled: auth.isAuthenticated,
    retry: false,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (me.isSuccess) clearSignInAttempt();
  }, [me.isSuccess]);

  if (auth.isLoading) {
    return <div className="flex min-h-screen items-center justify-center">Signing in…</div>;
  }
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  const code = unavailableCode(me.error);
  if (code) return <SignInProblem kind={code} />;
  if (me.isPending) {
    return <div className="flex min-h-screen items-center justify-center">Signing in…</div>;
  }
  return children;
}

export function AppRoutes({ embedded = false }: { embedded?: boolean }) {
  const layout = embedded ? <MainLayout embedded /> : <RequireAuth><MainLayout /></RequireAuth>;
  const allowed = (permission: string, page: React.ReactNode) => <RuntimePermissionRoute permission={permission}>{page}</RuntimePermissionRoute>;
  return (
    <Routes>
      {!embedded ? <Route path="login" element={<LoginPage />} /> : null}
      <Route element={layout}>
            <Route index element={allowed("pbx:read", <OverviewPage />)} />
            <Route path="config" element={<Navigate to="/config/contexts" replace />} />
            <Route path="config/contexts" element={allowed("pbx:read", <ContextsPage />)} />
            <Route path="config/contexts/:id" element={allowed("pbx:read", <ContextDetailPage />)} />
            <Route path="config/transports" element={allowed("pbx:read", <TransportsPage />)} />
            <Route path="config/auths" element={allowed("pbx:secrets", <AuthsPage />)} />
            <Route path="config/aors" element={allowed("pbx:read", <AorsPage />)} />
            <Route path="config/identifies" element={allowed("pbx:read", <IdentifiesPage />)} />
            <Route path="config/extensions" element={allowed("pbx:read", <ExtensionsPage />)} />
            <Route path="config/ari-apps" element={allowed("pbx:secrets", <AriAppsPage />)} />
            <Route path="config/websocket-endpoints" element={allowed("pbx:read", <WebsocketEndpointsPage />)} />
            <Route path="config/hep" element={allowed("pbx:secrets", <HepSettingsPage />)} />
            <Route path="config/view" element={allowed("pbx:secrets", <ConfigViewPage />)} />
            <Route path="calls/active" element={allowed("calls:read", <ActiveCallsPage />)} />
            <Route path="calls/history" element={allowed("calls:read", <CallHistoryPage />)} />
            <Route path="admin/ami-console" element={allowed("pbx:console", <AmiConsolePage />)} />
            <Route path="admin/logs" element={allowed("logs:read", <LogsPage />)} />
            <Route path="admin/config" element={allowed("pbx:secrets", <ConfigViewPage />)} />
            <Route path="admin/audit-logs" element={allowed("audit:read", <AuditLogsPage />)} />
            <Route path="admin" element={<Navigate to="/admin/ami-console" replace />} />
      </Route>
      {embedded ? <Route path="*" element={<Navigate to="/" replace />} /> : null}
    </Routes>
  );
}

function RuntimePermissionRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
  const runtime = runtimeSession();
  if (runtime && !runtime.permissions.includes(permission)) {
    return <div role="alert" className="p-6">Your current Unified permissions do not allow this page.</div>;
  }
  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
        <ThemedToaster />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
